import { Type, type Static } from "@sinclair/typebox";
import { AgentTurnInputSchema } from "./agent-turn.ts";
import {
  TemporaryFileReferenceSchema,
  TemporaryFileReferencesSchema
} from "./document-platform.ts";
import { PresentationCheckpointHintSchema } from "./ppt-expert.ts";
import { AgentKeySchema, MatrixUserIdSchema } from "./registered-agent.ts";
import { UuidSchema } from "./identifiers.ts";
import { UserAutomationExecutionEnvironmentSchema, UserAutomationContextReferenceSchema } from "./user-automation.ts";

export const WORKBENCH_AGENT_SCHEMA_VERSION = "1.0" as const;
export const WORKBENCH_AGENT_INVOCATION_EVENT_TYPE = "m.room.message" as const;
export const WORKBENCH_AGENT_REALTIME_TOPIC_PREFIX = "matrix-room:" as const;
export const AGENT_INVOCATION_CANCEL_REQUEST_KEY =
  "io.matrix_idp.agent.invocation_cancel_request" as const;
export const AGENT_WORKING_HOME_REFERENCE_LIMIT = 128;

export const MatrixEventIdSchema = Type.String({
  minLength: 4,
  maxLength: 512,
  pattern: "^\\$[^\\s]+$"
});
export const MatrixRoomIdSchema = Type.String({
  minLength: 4,
  maxLength: 512,
  pattern: "^![^\\s]+$"
});

export const AgentWorkingHomeReferencesSchema = Type.Array(TemporaryFileReferenceSchema, {
  maxItems: AGENT_WORKING_HOME_REFERENCE_LIMIT
});

export const AgentInvocationSourceSchema = Type.Union([
  Type.Object({ kind: Type.Literal("human"), senderMxid: MatrixUserIdSchema,
    executionEnvironment: Type.Optional(UserAutomationExecutionEnvironmentSchema) }, { additionalProperties: false }),
  Type.Object({
    kind: Type.Literal("user_automation"), automationId: UuidSchema,
    revision: Type.Integer({ minimum: 1 }), ownerPrincipalId: UuidSchema, ownerMxid: MatrixUserIdSchema,
    scheduledAt: Type.String({ format: "date-time" }), startedAt: Type.String({ format: "date-time" }),
    deadlineAt: Type.String({ format: "date-time" }),
    contextReferences: Type.Array(UserAutomationContextReferenceSchema, { maxItems: 32 }),
    timezone: Type.String({ minLength: 1, maxLength: 128 }),
    executionEnvironment: Type.Union([UserAutomationExecutionEnvironmentSchema, Type.Null()])
  }, { additionalProperties: false })
]);
export type AgentInvocationSource = Static<typeof AgentInvocationSourceSchema>;

export function actingHumanMxid(value: { source: AgentInvocationSource }): string {
  return value.source.kind === "human" ? value.source.senderMxid : value.source.ownerMxid;
}

export const AgentInvocationAuthorizationRequestBase = {
  agentKey: AgentKeySchema,
  sourceEventId: MatrixEventIdSchema,
  roomId: MatrixRoomIdSchema,
  source: AgentInvocationSourceSchema
};

const AgentInvocationEnvelopeBase = {
  sourceEventId: MatrixEventIdSchema,
  roomId: MatrixRoomIdSchema,
  source: AgentInvocationSourceSchema,
  turnInput: AgentTurnInputSchema,
  currentMessageFiles: Type.Optional(TemporaryFileReferencesSchema),
  workingHomeFiles: Type.Optional(AgentWorkingHomeReferencesSchema),
  checkpointHint: Type.Optional(PresentationCheckpointHintSchema),
  browserToolMode: Type.Optional(
    Type.Union([
      Type.Literal("program_authoring"),
      Type.Literal("conversation_execution")
    ])
  )
};

export const AgentInvocationEnvelopeSchema = Type.Union([
  Type.Object(
    {
      ...AgentInvocationEnvelopeBase,
      roomKind: Type.Literal("groupRoom"),
      threadRootEventId: MatrixEventIdSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      ...AgentInvocationEnvelopeBase,
      roomKind: Type.Literal("directChat"),
      threadRootEventId: Type.Union([MatrixEventIdSchema, Type.Null()])
    },
    { additionalProperties: false }
  )
]);

export const AgentInvocationCancelRequestSchema = Type.Object(
  {
    version: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION),
    targetSourceEventId: MatrixEventIdSchema
  },
  { additionalProperties: false }
);

export const AgentInvocationAuthorizationRequestSchema = Type.Union([
  Type.Object(
    {
      ...AgentInvocationAuthorizationRequestBase,
      roomKind: Type.Literal("groupRoom"),
      threadRootEventId: MatrixEventIdSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      ...AgentInvocationAuthorizationRequestBase,
      roomKind: Type.Literal("directChat"),
      threadRootEventId: Type.Union([MatrixEventIdSchema, Type.Null()])
    },
    { additionalProperties: false }
  )
]);

export const AgentInvocationAuthorizationResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION)
  },
  { additionalProperties: false }
);

const AgentAnswerDeltaEventBase = {
  version: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION),
  sourceEventId: MatrixEventIdSchema,
  sequence: Type.Integer({ minimum: 1, maximum: 1_000_000 })
};

export const AgentAnswerDeltaEventSchema = Type.Union([
  Type.Object(
    {
      ...AgentAnswerDeltaEventBase,
      operation: Type.Literal("reset")
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      ...AgentAnswerDeltaEventBase,
      operation: Type.Literal("append"),
      delta: Type.String({ minLength: 1, maxLength: 4_096 })
    },
    { additionalProperties: false }
  )
]);

export const AgentThinkingEventSchema = Type.Union([
  Type.Object(
    {
      ...AgentAnswerDeltaEventBase,
      operation: Type.Union([
        Type.Literal("started"),
        Type.Literal("completed"),
        Type.Literal("reset")
      ])
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      ...AgentAnswerDeltaEventBase,
      operation: Type.Literal("append"),
      delta: Type.String({ minLength: 1, maxLength: 4_096 })
    },
    { additionalProperties: false }
  )
]);

export const AgentRealtimeTokenRequestSchema = Type.Object(
  {
    roomIds: Type.Array(MatrixRoomIdSchema, {
      minItems: 1,
      maxItems: 100,
      uniqueItems: true
    }),
    browserDeviceIds: Type.Optional(
      Type.Array(UuidSchema, {
        maxItems: 16,
        uniqueItems: true
      })
    )
  },
  { additionalProperties: false }
);

export const AgentRealtimeTokenResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION),
    token: Type.String({ minLength: 32, maxLength: 32_768 }),
    expiresAt: Type.String({ format: "date-time" })
  },
  { additionalProperties: false }
);

export type AgentInvocationEnvelopeV1 = Static<typeof AgentInvocationEnvelopeSchema>;
export type AgentInvocationCancelRequestV1 = Static<typeof AgentInvocationCancelRequestSchema>;
export type AgentInvocationAuthorizationRequestV1 = Static<
  typeof AgentInvocationAuthorizationRequestSchema
>;
export type AgentInvocationAuthorizationResultV1 = Static<
  typeof AgentInvocationAuthorizationResultSchema
>;
export type AgentAnswerDeltaEventV1 = Static<typeof AgentAnswerDeltaEventSchema>;
export type AgentThinkingEventV1 = Static<typeof AgentThinkingEventSchema>;
export type AgentRealtimeTokenRequestV1 = Static<typeof AgentRealtimeTokenRequestSchema>;
export type AgentRealtimeTokenResultV1 = Static<typeof AgentRealtimeTokenResultSchema>;
